/**
 * A gap, drawn: what the rest of the network sells, and what this market does.
 *
 * The finding says "Lemonade at 0.1% against an 11.9% norm". Two numbers in a
 * sentence make a reader do the arithmetic and then trust their own answer; two
 * bars on a shared scale make the size of the miss the first thing they see.
 *
 * **The benchmark excludes this market.** It is the same distributor's line mix
 * across every *other* metro — leave-one-out — so a distributor with a national
 * skew is not flagged everywhere for having that skew, and a market is never
 * compared against a number it is itself inside. The grey bar is that
 * benchmark; the coloured bar is this market.
 *
 * Nothing is computed here. `metro_pct`, `channel_norm_pct` and
 * `pct_of_channel_norm` all arrive from the payload.
 */

const TRACK = 'gs-bar-track'

export interface GapBar {
  readonly key: string
  readonly market: string
  readonly line: string
  readonly gapType: string
  readonly metroPct: number | null
  readonly normPct: number | null
  readonly pctOfNorm: number | null
  readonly gallonsAtRisk: number | null
  readonly distributors: number
  readonly notCarrying: number
}

interface Props {
  readonly gaps: readonly GapBar[]
  readonly onPick: (market: string) => void
}

/** Bars share one scale so two gaps can be compared by eye, not just read. */
function scaleOf(gaps: readonly GapBar[]): number {
  const peak = Math.max(
    ...gaps.flatMap((g) => [g.metroPct ?? 0, g.normPct ?? 0]),
    1,
  )
  return peak * 1.05
}

function pct(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(1)}%`
}

function gal(value: number | null): string {
  return value === null ? '—' : Math.round(value).toLocaleString('en-US')
}

export function GapBars({ gaps, onPick }: Props) {
  if (gaps.length === 0) {
    return <div className="state">No open gaps.</div>
  }
  const scale = scaleOf(gaps)

  return (
    <ul className="gs-bars">
      {gaps.map((gap) => {
        const normWidth = ((gap.normPct ?? 0) / scale) * 100
        const metroWidth = ((gap.metroPct ?? 0) / scale) * 100
        const severe = (gap.pctOfNorm ?? 100) < 25
        return (
          <li className="gs-bar-row" key={gap.key}>
            <div className="gs-bar-head">
              <button
                type="button"
                className="gs-bar-title"
                onClick={() => {
                  onPick(gap.market)
                }}
              >
                <strong>{gap.line}</strong>
                <span className="gs-bar-market">{gap.market}</span>
              </button>
              <span className={severe ? 'gs-bar-share is-severe' : 'gs-bar-share'}>
                {gap.pctOfNorm === null ? '—' : `${String(Math.round(gap.pctOfNorm))}%`}
                <span className="gs-bar-share-label">of norm</span>
              </span>
            </div>

            <div className={TRACK}>
              {/* the benchmark: this distributor everywhere except here */}
              <div className="gs-bar gs-bar-norm" style={{ width: `${String(normWidth)}%` }}>
                <span className="gs-bar-tick" />
              </div>
              {/* what this market actually does */}
              <div
                className={severe ? 'gs-bar gs-bar-metro is-severe' : 'gs-bar gs-bar-metro'}
                style={{ width: `${String(metroWidth)}%` }}
              />
            </div>

            <p className="gs-bar-foot">
              <span>
                here <strong>{pct(gap.metroPct)}</strong>
              </span>
              <span>
                norm elsewhere <strong>{pct(gap.normPct)}</strong>
              </span>
              <span>
                <strong>{gal(gap.gallonsAtRisk)}</strong> gal at risk
              </span>
              <span>
                {gap.distributors} distributor{gap.distributors === 1 ? '' : 's'}
                {gap.notCarrying > 0 && `, ${String(gap.notCarrying)} carrying none`}
              </span>
              <span className="gs-bar-kind">{gap.gapType}</span>
            </p>
          </li>
        )
      })}
    </ul>
  )
}

export interface CompositionSlice {
  readonly bucket: string
  readonly gaps: number
  readonly gallons: number | null
  readonly sharePct: number | null
}

/** What the gap is made of, along one dimension. */
export function Composition({
  slices,
  onPick,
}: {
  readonly slices: readonly CompositionSlice[]
  readonly onPick?: ((bucket: string) => void) | undefined
}) {
  if (slices.length === 0) {
    return <div className="state">Nothing to break down.</div>
  }
  const peak = Math.max(...slices.map((s) => s.gallons ?? 0), 1)

  return (
    <ul className="gs-comp">
      {slices.map((slice) => (
        <li className="gs-comp-row" key={slice.bucket}>
          <span className="gs-comp-label">
            {onPick === undefined ? (
              slice.bucket
            ) : (
              <button
                type="button"
                className="gs-link"
                onClick={() => {
                  onPick(slice.bucket)
                }}
              >
                {slice.bucket}
              </button>
            )}
          </span>
          <span className="gs-comp-track">
            <span
              className="gs-comp-fill"
              style={{ width: `${String(((slice.gallons ?? 0) / peak) * 100)}%` }}
            />
          </span>
          <span className="gs-comp-value cell-number">{gal(slice.gallons)}</span>
          <span className="gs-comp-share cell-number">
            {slice.sharePct === null ? '—' : `${slice.sharePct.toFixed(0)}%`}
          </span>
          <span className="gs-comp-count cell-number">
            {slice.gaps} gap{slice.gaps === 1 ? '' : 's'}
          </span>
        </li>
      ))}
    </ul>
  )
}
